import { useState } from "react";
import { useAuth } from "@/App";
import { Slider } from "@/components/ui/slider";
import { ChevronRight, Zap, Bike, Dumbbell, Timer, Heart, Target, Flame, TrendingUp, Activity, Droplet } from "lucide-react";
import { toast } from "sonner";

const SPORTS = [
  { id: "running", name: "Futás", icon: Activity, desc: "Út & terep" },
  { id: "cycling", name: "Kerékpár", icon: Bike, desc: "Közúti & MTB" },
  { id: "strength", name: "Erőedzés", icon: Dumbbell, desc: "Súlyzós edzés" },
  { id: "bodyweight", name: "Saját testsúlyos", icon: Flame, desc: "Calisthenics & core" },
  { id: "swimming", name: "Úszás", icon: Droplet, desc: "Medence & nyílt víz" },
  { id: "triathlon", name: "Triatlon", icon: Timer, desc: "Úszás, kerékpár, futás" },
  { id: "fitness", name: "Fitness", icon: Heart, desc: "Általános fitnesz" },
];

const GOALS = [
  { id: "performance", name: "Teljesítmény növelés", icon: TrendingUp, desc: "Maximalizáld az eredményeid" },
  { id: "endurance", name: "Állóképesség", icon: Activity, desc: "Hosszabb, kitartóbb edzések" },
  { id: "strength", name: "Erő", icon: Dumbbell, desc: "Erőnövelés és izomépítés" },
  { id: "fatburn", name: "Zsírégetés", icon: Flame, desc: "Testösszetétel optimalizálás" },
  { id: "longevity", name: "Longevity fitness", icon: Heart, desc: "Élj hosszabb, egészségesebb életet" },
];

export default function OnboardingPage({ onComplete }) {
  const { api } = useAuth();
  const [step, setStep] = useState(0);
  const [sports, setSports] = useState([]);
  const [goal, setGoal] = useState("");
  const [weeklyHours, setWeeklyHours] = useState([5]);
  const [saving, setSaving] = useState(false);

  const handleFinish = async () => {
    setSaving(true);
    try {
      await api().post("/onboarding", { sports, goal, weekly_hours: weeklyHours[0] });
      toast.success("Profil létrehozva! Kezdjük el!");
      onComplete();
    } catch {
      toast.error("Hiba történt");
    } finally {
      setSaving(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return sports.length > 0;
    if (step === 2) return goal !== "";
    return true;
  };

  return (
    <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center px-4" data-testid="onboarding-page">
      <div className="w-full max-w-lg">
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[0, 1, 2, 3].map(i => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === step ? "w-8 bg-[#00f260]" : i < step ? "w-4 bg-[#00f260]/50" : "w-4 bg-white/10"
              }`}
            />
          ))}
        </div>

        {/* Step 0: Welcome */}
        {step === 0 && (
          <div className="text-center fade-in-up" data-testid="onboarding-step-0">
            <div className="w-24 h-24 rounded-full bg-[#00f260]/10 flex items-center justify-center mx-auto mb-6">
              <Zap size={40} className="text-[#00f260]" />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-4">
              Üdv az Athlevity-ben
            </h1>
            <p className="text-gray-400 text-base md:text-lg leading-relaxed mb-8 max-w-md mx-auto">
              Optimalizáld a teljesítményed és élj hosszabb, energikusabb életet.
            </p>
            <button
              data-testid="onboarding-start-btn"
              onClick={() => setStep(1)}
              className="neon-btn text-sm px-8 py-3"
            >
              Kezdés
            </button>
          </div>
        )}

        {/* Step 1: Sport */}
        {step === 1 && (
          <div className="fade-in-up" data-testid="onboarding-step-1">
            <h2 className="font-heading text-3xl font-bold text-white text-center mb-2">Válaszd ki sportjaid</h2>
            <p className="text-gray-400 text-sm text-center mb-6">Több sportot is kijelölhetsz</p>
            <div className="space-y-3">
              {SPORTS.map(s => {
                const Icon = s.icon;
                const selected = sports.includes(s.id);
                return (
                  <button
                    key={s.id}
                    data-testid={`sport-${s.id}`}
                    onClick={() =>
                      setSports(prev => prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id])
                    }
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all ${
                      selected
                        ? "bg-[#00f260]/10 border-[#00f260]/50 shadow-[0_0_15px_rgba(0,242,96,0.15)]"
                        : "bg-white/5 border-white/10 hover:border-white/20"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${selected ? "bg-[#00f260]/20" : "bg-white/5"}`}>
                      <Icon size={20} className={selected ? "text-[#00f260]" : "text-gray-400"} />
                    </div>
                    <div className="text-left flex-1">
                      <p className={`text-sm font-medium ${selected ? "text-[#00f260]" : "text-white"}`}>{s.name}</p>
                      <p className="text-xs text-gray-500">{s.desc}</p>
                    </div>
                    {selected && <div className="w-5 h-5 rounded-full bg-[#00f260] flex items-center justify-center"><ChevronRight size={12} className="text-black" /></div>}
                  </button>
                );
              })}
            </div>
            <button
              data-testid="onboarding-next-1"
              onClick={() => setStep(2)}
              disabled={!canProceed()}
              className={`neon-btn w-full text-sm mt-6 ${!canProceed() ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              Tovább
            </button>
          </div>
        )}

        {/* Step 2: Goal */}
        {step === 2 && (
          <div className="fade-in-up" data-testid="onboarding-step-2">
            <h2 className="font-heading text-3xl font-bold text-white text-center mb-2">Mi a célod?</h2>
            <p className="text-gray-400 text-sm text-center mb-6">Válaszd ki a fő célodat</p>
            <div className="space-y-3">
              {GOALS.map(g => {
                const Icon = g.icon;
                const selected = goal === g.id;
                return (
                  <button
                    key={g.id}
                    data-testid={`goal-${g.id}`}
                    onClick={() => setGoal(g.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all ${
                      selected
                        ? "bg-[#00f260]/10 border-[#00f260]/50 shadow-[0_0_15px_rgba(0,242,96,0.15)]"
                        : "bg-white/5 border-white/10 hover:border-white/20"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${selected ? "bg-[#00f260]/20" : "bg-white/5"}`}>
                      <Icon size={20} className={selected ? "text-[#00f260]" : "text-gray-400"} />
                    </div>
                    <div className="text-left flex-1">
                      <p className={`text-sm font-medium ${selected ? "text-[#00f260]" : "text-white"}`}>{g.name}</p>
                      <p className="text-xs text-gray-500">{g.desc}</p>
                    </div>
                    {selected && <div className="w-5 h-5 rounded-full bg-[#00f260] flex items-center justify-center"><ChevronRight size={12} className="text-black" /></div>}
                  </button>
                );
              })}
            </div>
            <button
              data-testid="onboarding-next-2"
              onClick={() => setStep(3)}
              disabled={!canProceed()}
              className={`neon-btn w-full text-sm mt-6 ${!canProceed() ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              Tovább
            </button>
          </div>
        )}

        {/* Step 3: Weekly hours */}
        {step === 3 && (
          <div className="fade-in-up" data-testid="onboarding-step-3">
            <h2 className="font-heading text-3xl font-bold text-white text-center mb-2">Heti edzésidő</h2>
            <p className="text-gray-400 text-sm text-center mb-8">Hány órát tudsz hetente edzésre szánni?</p>

            <div className="glass-card p-8 text-center mb-8">
              <p className="font-heading text-6xl font-bold text-[#00f260] neon-text mb-2">
                {weeklyHours[0]}
              </p>
              <p className="text-gray-400 text-sm">óra / hét</p>

              <div className="mt-8 px-4">
                <Slider
                  data-testid="weekly-hours-slider"
                  value={weeklyHours}
                  onValueChange={setWeeklyHours}
                  min={0}
                  max={10}
                  step={0.5}
                  className="w-full"
                />
                <div className="flex justify-between mt-2">
                  <span className="text-xs text-gray-500">0 óra</span>
                  <span className="text-xs text-gray-500">10 óra</span>
                </div>
              </div>
            </div>

            <button
              data-testid="onboarding-finish-btn"
              onClick={handleFinish}
              disabled={saving}
              className="neon-btn w-full text-sm"
            >
              {saving ? "Mentés..." : "Profil létrehozása"}
            </button>
          </div>
        )}

        {/* Back button */}
        {step > 0 && (
          <button
            data-testid="onboarding-back-btn"
            onClick={() => setStep(step - 1)}
            className="w-full text-center text-gray-400 text-sm mt-4 hover:text-white transition-colors"
          >
            Vissza
          </button>
        )}
      </div>
    </div>
  );
}
